import { CalendarIcon, EmailIcon } from '@chakra-ui/icons'
import {
  Box,
  BoxProps,
  Button,
  Center,
  CircularProgress,
  Divider,
  Flex,
  FlexProps,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  Tooltip,
  useColorMode,
  VStack,
} from '@chakra-ui/react'
import {
  AppData,
  BaseColumnRenderProps,
  DataColumn,
  useAssessmentThread,
  usePatientThread,
} from '@hb/react'
import {
  ASSESSMENT_SNIPPETS,
  ASSESSMENTS_ADMIN,
  AssessmentSnippet,
  colors,
  Delivery,
  getCoverageNextActionEntity,
  getCoverageNextActions,
  getDateString,
  getPracticeAssessmentCollectionPath,
  gynNextActionEntity,
  ListNextActionEntity,
  NextAction,
  NextActionType,
  optionalInsuranceProviderMultipleSelect,
  patientColor,
  PatientCoverageId,
  PopulatedPregnancyPlan,
  PracticeAssessmentData,
  stringifyNextAction,
  ThreadMessage,
  toSearchString,
  WithId,
  WithMetadata,
} from '@hb/shared'
import { PropsWithChildren, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDataListContext } from '../../../../contexts/DataListContext'
import cashIcon from '../../../../icons/cash.svg'
import { ActionButton } from '../../../Buttons'
import { getRowBackground } from '../../../DataView'
import { EllipsisText } from '../../../EllipsisText'
import { RecentMessageView } from '../../../Messaging/ThreadView/Messages/RecentMessageView'
import {
  CollectionNextActionContent,
  EntityNextActionContent,
} from '../../../Users/Profile/NextAction'
import { FieldSelectHeader } from '../../PresetFilters'
import { SortButton } from '../../SortButton'
import { AssignedAdmins, AssignedAdminsHeader, AssignedAdminsItem } from './AssignedAdminsPopover'
import { AdminAssessmentColumn, AssessmentColumn } from './types'
import {
  PracticeUnreadMessagesBadge,
  UnreadMessagesBadge,
  UnreadMessagesHeaderBadge,
} from './UnreadMessageBadge'

const getMatchesQuery = (
  nextAction: NextAction | undefined | null,
  searchQuery: string | undefined,
  entity: ListNextActionEntity | null,
) => {
  if (!searchQuery) return false
  const stringified = stringifyNextAction(nextAction, entity)
  const stringifiedQuery = toSearchString(searchQuery)
  return stringified.includes(stringifiedQuery)
}
export const CollectionNextActionPopover = ({
  item,
  collection,
  searchQuery,
  label,
}: {
  collection: string
  label: string
  item: WithId
  searchQuery?: string
}) => {
  const { nextActionText, nextAction, id } = item ?? {}
  const { text: baseText } = nextAction ?? {}
  const matchesQuery = getMatchesQuery(nextAction ?? { text: nextActionText }, searchQuery, null)
  const text = nextActionText || baseText
  const bg = matchesQuery ? 'green.400' : 'gray.50'
  const darkBg = matchesQuery ? 'green.600' : 'gray.800'
  const color = matchesQuery ? 'white' : 'blue.600'
  const darkColor = matchesQuery ? 'white' : 'blue.200'
  const { colorMode } = useColorMode()

  return (
    <Box
      w="100%"
      onClick={e => {
        e.stopPropagation()
      }}>
      <Popover strategy="fixed" isLazy>
        {({ isOpen }) => (
          <>
            <PopoverTrigger>
              <Button
                w="100%"
                justifyContent="flex-start"
                size="sm"
                h={8}
                boxShadow={`0 0 4px ${matchesQuery ? colors.green.hex : '#00000044'}`}
                bg={isOpen ? 'blue.600' : bg}
                color={isOpen ? 'white' : color}
                _dark={{
                  bg: isOpen ? 'blue.400' : darkBg,
                  color: isOpen ? 'white' : darkColor,
                  _hover: { bg: isOpen ? 'blue.600' : 'blue.400', color: 'white' },
                }}
                _hover={{
                  bg: isOpen ? 'blue.600' : 'blue.400',
                  color: 'white',
                }}>
                <Text
                  position="relative"
                  // width={itemWidth}
                  height="18px"
                  textAlign="left"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  fontWeight={400}
                  fontSize="sm">
                  {!text || text === 'zzz' ? 'None' : text}
                </Text>
              </Button>
            </PopoverTrigger>
            <Portal>
              <PopoverContent bg="gray.50" w="auto" _dark={{ bg: 'gray.800' }}>
                <PopoverBody>
                  <Text fontWeight={500}>{label} Next Action</Text>
                  <Flex w="350px">
                    <CollectionNextActionContent
                      id={id}
                      inList
                      collection={collection}
                      item={item as any}
                    />
                  </Flex>
                </PopoverBody>
                <PopoverCloseButton />
                <PopoverArrow bg={colorMode === 'light' ? 'gray.50' : 'gray.800'} />
              </PopoverContent>
            </Portal>
          </>
        )}
      </Popover>
    </Box>
  )
}

const nextActionPopoverContentFlexProps: FlexProps = {
  flexFlow: 'column-reverse',
  gap: 0,
}

const nextActionDateContainerProps: BoxProps = {
  // py: 1,
}

const popoverBoxShadow = (matchesQuery: boolean) =>
  `0 0 4px ${matchesQuery ? colors.green.hex : '#00000022'}`

const ListNextActionPopover = ({
  nextAction,
  // patientId,
  searchQuery,
  entity,
  pregnancyId,
  label,
}: {
  nextAction: NextAction | undefined | null
  // patientId: string
  searchQuery: string
  pregnancyId: string | null
  entity: ListNextActionEntity
  label: string
}) => {
  const { text } = nextAction ?? {}
  const matchesQuery = getMatchesQuery(nextAction, searchQuery, entity)
  const bg = matchesQuery ? 'green.400' : 'gray.50'
  const color = matchesQuery ? 'white' : 'blue.600'
  const boxShadow = popoverBoxShadow(matchesQuery)
  return (
    <Popover strategy="fixed" isLazy>
      {({ isOpen }) => (
        <>
          <PopoverTrigger>
            <Button
              w="100%"
              justifyContent="flex-start"
              size="sm"
              h={9}
              position="relative"
              bg={isOpen ? 'blue.600' : bg}
              _hover={{
                bg: isOpen ? 'blue.400' : 'blue.200',
                color: 'white',
              }}
              style={{ boxShadow }}>
              <Text
                position="relative"
                top="6px"
                // width={itemWidth}
                height="18px"
                textAlign="left"
                color={isOpen ? 'white' : color}
                overflow="hidden"
                textOverflow="ellipsis"
                fontWeight={400}
                fontSize="sm">
                {!text || text === 'zzz' ? 'None' : text}
              </Text>
              <Text
                fontSize="xs"
                textShadow={matchesQuery || isOpen ? '1px 1px 3px #00000055' : 'none'}
                color={matchesQuery || isOpen ? 'gray.100' : 'gray.500'}
                opacity={0.7}
                top="2px"
                position="absolute">
                {label}
              </Text>
            </Button>
          </PopoverTrigger>
          <PopoverContent bg="gray.50" w="auto">
            <PopoverArrow bg="gray.50" />
            <PopoverBody p={0}>
              <Text px={2} py={1} fontWeight={500}>
                {label} Next Action
              </Text>
              <Flex w="350px">
                <EntityNextActionContent
                  // patientId={patientId}
                  inList
                  assessmentId={pregnancyId}
                  flexProps={nextActionPopoverContentFlexProps}
                  dateContainerProps={nextActionDateContainerProps}
                  nextAction={nextAction}
                  entity={entity}
                />
              </Flex>
            </PopoverBody>
            <PopoverCloseButton />
          </PopoverContent>
        </>
      )}
    </Popover>
  )
}

const getLabelPrefix = (pregnancy: AssessmentSnippet, id: PatientCoverageId) => {
  if (pregnancy.plans?.primary?.id === id) return 'Primary'
  if (pregnancy.plans?.secondary?.id === id) return 'Secondary'
  return 'Potential Plan'
}
const ListCoverageNextActionPopover = ({
  id,
  coverage,
  pregnancy,
  ...props
}: {
  nextAction: NextAction | undefined
  id: PatientCoverageId
  patientId: string
  coverage: PopulatedPregnancyPlan | undefined | null
  pregnancy: AssessmentSnippet
  searchQuery: string
  pregnancyId: string | null
}) => {
  const labelPrefix = getLabelPrefix(pregnancy, id)
  const label = useMemo(
    () => `${labelPrefix.toUpperCase()} - ${coverage?.insuranceProvider?.name || 'No Insurer'}`,
    [coverage, labelPrefix],
  )

  const entity = getCoverageNextActionEntity(id, coverage)

  return <ListNextActionPopover entity={entity} label={label} {...props} />
}

export const getRenderItemNextAction =
  <T extends NextActionType>(collection: string, label: string): DataColumn<T>['Render'] =>
  ({ data, searchQuery }) => (
    <Flex pr={2} gap={1} w="100%" align="center">
      <CollectionNextActionPopover
        label={label}
        searchQuery={searchQuery}
        collection={collection}
        item={data}
      />
    </Flex>
  )

const RenderCoverageNextActions: AdminAssessmentColumn['Render'] = ({ data, searchQuery }) => {
  const nextActionsArr = getCoverageNextActions(data.plans, data.nextActions).sort(
    (a, b) => a.date - b.date,
  )
  return (
    <Flex gap={1} w="100%" flexFlow="column" align="center">
      {nextActionsArr.map(({ id: coverageId, coverage, nextAction }, idx) => (
        <Box flex={1} key={`${coverageId}_${idx}`} w="100%">
          <ListCoverageNextActionPopover
            pregnancyId={data.id}
            pregnancy={data}
            patientId={data.patientId}
            coverage={coverage}
            id={coverageId}
            searchQuery={searchQuery}
            nextAction={nextAction ?? undefined}
          />
        </Box>
      ))}
    </Flex>
  )
}

const RenderGynNextAction: AdminAssessmentColumn['Render'] = ({ data, searchQuery }) => {
  const { gynNextAction, id } = data
  return (
    <Flex pr={2} gap={1} w="100%" align="center">
      <ListNextActionPopover
        label="GYN"
        pregnancyId={id}
        entity={gynNextActionEntity}
        searchQuery={searchQuery}
        nextAction={gynNextAction}
      />
    </Flex>
  )
}

export const RenderAssessmentNextAction: AdminAssessmentColumn['Render'] = props => {
  const { data } = props
  if (data.isGynProfile) return RenderGynNextAction(props)
  return RenderCoverageNextActions(props)
}

export const getRenderPracticeAssessmentNextAction = (practiceId: string) =>
  getRenderItemNextAction<PracticeAssessmentData>(
    getPracticeAssessmentCollectionPath(practiceId),
    'Pregnancy',
  )

export const insurerColumn: AssessmentColumn = {
  title: 'INSURER',
  // Header: () => <SortButton sortKey="insuranceCoverage">INSURER</SortButton>,
  Header: () => (
    <FieldSelectHeader
      propName="insurerId"
      label="INSURER"
      field={optionalInsuranceProviderMultipleSelect}
      sortPropName="insuranceCoverage"
      defaultDir="asc"
    />
  ),
  sortKey: 'insuranceCoverage',
  Render: ({ data }) => (
    <EllipsisText
      withTooltip
      // maxWidth={itemWidth}
      text={
        'insuranceCoverage' in data && data?.insuranceCoverage && data.insuranceCoverage !== 'zzz'
          ? `${data?.insuranceCoverage}${data.planState ? ` | ${data.planState}` : ''}`
          : 'No coverage'
      }
    />
  ),
}

interface DeliveredOnItem {
  deliveredOn: number | null
  delivery?: WithMetadata<Partial<Delivery>> | null
}
export const getDeliveredOnColumn = <T extends DeliveredOnItem>(): DataColumn<T> => ({
  title: 'Date of Delivery',
  Header: () => (
    <Flex align="center" flexGrow={1}>
      <SortButton defaultDir="asc" sortKey="deliveredOn">
        <CalendarIcon />
        <Text>DELIVERY</Text>
      </SortButton>
    </Flex>
  ),
  width: 112,
  sortKey: 'deliveredOn',
  defaultSortDirection: 'desc',
  Render: props => {
    let text = 'None'
    if (props.data?.deliveredOn && props.data?.deliveredOn !== Infinity) {
      text = getDateString(props.data.deliveredOn, 'short')
    } else if (props.data?.delivery?.isTransfer || props.data?.deliveredOn === Infinity) {
      text = 'Transfer'
    }
    return (
      <Stack direction="row" spacing={2} pl={1} pr={1} width="100%" align="center">
        <Text
          opacity={props.data?.deliveredOn || props?.data?.delivery?.isTransfer ? 1 : 0.7}
          flex={1}>
          {text}
        </Text>
      </Stack>
    )
  },
  mobile: {
    width: 160,
    Render: ({ data }) => {
      const { deliveredOn } = data ?? {}
      return (
        <Center
          py={1}
          boxShadow="1px 1px 3px #00000077"
          gap={1}
          bg="gray.100"
          h="28px"
          px={2}
          borderRadius={4}
          w="100%"
          flex={1}>
          <Text
            lineHeight={1}
            fontFamily="Hero-New"
            fontSize="xs"
            fontWeight={600}
            color="gray.500">
            DELIVERED
          </Text>
          <Text ml="auto" lineHeight={1} fontFamily="Open Sans" fontSize="sm" color="gray.600">
            {deliveredOn ? getDateString(deliveredOn, 'short') : 'No'}
          </Text>
        </Center>
      )
    },
  },
})

const OverduePaymentIndicator = ({
  hasOverduePayment,
  ...props
}: BoxProps & { hasOverduePayment?: boolean }) => (
  <Box px={1} {...props}>
    <Tooltip
      placement="top"
      hasArrow
      bg={hasOverduePayment ? colors.green.hex : 'yellow.600'}
      label={hasOverduePayment ? 'Overdue payment' : 'No overdue payments'}>
      <Image
        opacity={hasOverduePayment ? 1 : 0.7}
        filter={`grayscale(${hasOverduePayment ? 0 : 100}%)`}
        width="20px"
        alt="payment past due"
        src={cashIcon}
      />
    </Tooltip>
  </Box>
)

interface HasOverduePaymentItem {
  hasOverduePayment?: boolean
}

export const getOverduePaymentColumn = <T extends HasOverduePaymentItem>(): DataColumn<T> => ({
  title: 'Overdue Payment',
  Header: () => {
    const { sort } = useDataListContext()
    return (
      <Stack align="center" direction="row" width="100%">
        <SortButton defaultDir="desc" noArrow sortKey="hasOverduePayment">
          <OverduePaymentIndicator hasOverduePayment={sort.field === 'hasOverduePayment'} />
        </SortButton>
      </Stack>
    )
  },
  // flexProps: { width: '100%' },
  width: 40,
  sortKey: 'hasOverduePayment',
  defaultSortDirection: 'desc',
  Render: ({ data: { hasOverduePayment } }) => (
    <Center w="40px">
      <OverduePaymentIndicator hasOverduePayment={hasOverduePayment} />
    </Center>
  ),
  mobile: {
    width: 130,
    Render: ({ data }) => {
      const { hasOverduePayment } = data ?? {}
      return (
        <Center
          py={1}
          boxShadow="1px 1px 3px #00000077"
          gap={1}
          bg={hasOverduePayment ? 'yellow.500' : 'gray.100'}
          borderRadius={4}
          w="100%"
          h="28px"
          flex={1}>
          <OverduePaymentIndicator hasOverduePayment={hasOverduePayment} />
          <Text
            textShadow={hasOverduePayment ? '1px 1px 3px #00000077' : 'none'}
            lineHeight={1}
            fontFamily="Hero-New"
            fontSize="xs"
            fontWeight={600}
            color={hasOverduePayment ? 'white' : 'gray.500'}>
            {hasOverduePayment ? 'OVERDUE' : 'NOT OVERDUE'}
          </Text>
        </Center>
      )
    },
  },
})

const UnreadMessagesSectionPreview = ({
  type,
  loading,
  mostRecent,
  viewButton,
}: {
  type: 'patient' | 'practice'
  loading: boolean
  mostRecent: ThreadMessage[]
  viewButton: React.ReactNode | null
}) => (
  <>
    <Flex
      bg={type === 'patient' ? `${patientColor}77` : `${colors.green.hex}33`}
      height={8}
      py={1}
      pl={2}
      pr={10}
      align="center"
      w="100%"
      gap={2}>
      <Text color="gray.600" fontWeight={500} fontFamily="Hero-New">
        {type === 'patient' ? 'Patient Messages' : 'Practice Messages'}
      </Text>
      <CircularProgress
        isIndeterminate={loading}
        opacity={loading ? 1 : 0}
        transition="opacity 300ms"
        size={4}
        color="green"
      />
      {viewButton}
    </Flex>
    {mostRecent.length ? (
      mostRecent.map((message, i) => (
        <Box bg={getRowBackground(i)} px={2} key={`${message.createdOn}`} py={1} w="100%">
          <RecentMessageView vertical message={message} />
        </Box>
      ))
    ) : (
      <Text textAlign="center" w="100%" bg="whiteAlpha.500" fontSize="md" p={2} fontStyle="italic">
        No unread messages
      </Text>
    )}
  </>
)

const UnreadMessagePreviewContent = <
  T extends { unreadPregnancyMessages?: number; unreadPatientMessages?: number },
>({
  assessmentId,
  patientId,
  item,
  tabName,
  app,
  patientUnread,
  practiceUnread,
  practiceId,
}: {
  assessmentId: string
  patientId: string
  item: WithId<T>
  tabName: string
  practiceId: string | null
  app: AppData
  patientUnread: boolean
  practiceUnread: boolean
}) => {
  const { data: thread, loading } = useAssessmentThread(assessmentId)
  const { data: patientThread, loading: patientLoading } = usePatientThread(
    practiceId ? null : patientId,
  )

  const mostRecent = useMemo(
    () => (thread?.mostRecentMessages ? thread.mostRecentMessages.slice(0, 3) : []),
    [thread],
  )

  const mostRecentPatient = useMemo(
    () => (patientThread?.mostRecentMessages ? patientThread.mostRecentMessages.slice(0, 3) : []),
    [patientThread],
  )

  const displayPatientFirst = patientUnread && !practiceUnread
  const viewButton = (
    <Link
      style={{
        marginLeft: 'auto',
      }}
      to={`${
        app.appName === 'app' ? '/admin' : ''
      }/pregnancies/${item.id}/${tabName === 'Claim' ? 'claims' : 'assessment'}`}>
      <ActionButton size="xs" colorScheme="green" bg="white" borderRadius="full">
        View Assessment
      </ActionButton>
    </Link>
  )
  const practicePreview = (
    <UnreadMessagesSectionPreview
      type="practice"
      loading={loading}
      mostRecent={mostRecent}
      viewButton={displayPatientFirst ? null : viewButton}
    />
  )

  const patientPreview = practiceId ? null : (
    <UnreadMessagesSectionPreview
      type="patient"
      loading={patientLoading}
      mostRecent={mostRecentPatient}
      viewButton={displayPatientFirst ? viewButton : null}
    />
  )

  return (
    <Flex flexFlow="column" w="350px">
      <VStack w="100%" divider={<Divider />} spacing={0}>
        {displayPatientFirst ? patientPreview : practicePreview}
        {displayPatientFirst ? practicePreview : patientPreview}
      </VStack>
      {/* <CollectionNextActionContent id={id} collection={collection} item={item as any} /> */}
    </Flex>
  )
}

const UnreadMessagePreviewPopover = <
  T extends {
    unreadThreadMessages?: number
    unreadPregnancyMessages?: number
    unreadPatientMessages?: number
    patientId: string
    assessmentId?: string
    practiceId?: string
  },
>({
  children,
  data: item,
  practiceId,
  tabName,
  app,
  mobile,
}: PropsWithChildren<
  BaseColumnRenderProps<WithId<T>, { mobile?: boolean; practiceId: string | null }>
>) => (
  <Popover placement="right" trigger={mobile ? 'click' : 'hover'} strategy="fixed" isLazy>
    <PopoverTrigger>{children}</PopoverTrigger>
    <PopoverContent p={0} bg="gray.50" w="auto">
      <PopoverArrow bg="gray.50" />
      <UnreadMessagePreviewContent
        app={app}
        patientUnread={!!item.unreadPatientMessages}
        practiceUnread={practiceId ? !!item.unreadThreadMessages : !!item.unreadPregnancyMessages}
        practiceId={practiceId}
        tabName={tabName}
        item={item}
        assessmentId={item.assessmentId ?? item.id}
        patientId={item.patientId}
      />
      <PopoverCloseButton />
    </PopoverContent>
  </Popover>
)

interface UnreadThreadMessagesItem {
  unreadThreadMessages: number
  unreadPregnancyMessages?: number
  unreadPatientMessages?: number
  patientId: string
}
export const getUnreadMessagesColumn = <Data extends UnreadThreadMessagesItem>(
  practiceId: string | null,
): DataColumn<Data> => ({
  title: 'Unread Messages',
  Header: () => {
    const { sort } = useDataListContext()
    return (
      <Center width="40px">
        <SortButton defaultDir="desc" noArrow sortKey="unreadThreadMessages">
          <Tooltip placement="top" hasArrow label="Unread Messages">
            <Box>
              <UnreadMessagesHeaderBadge active={sort.field === 'unreadThreadMessages'} />
            </Box>
          </Tooltip>
        </SortButton>
      </Center>
    )
  },
  defaultSortDirection: 'asc',
  width: 36,
  sortKey: 'unreadThreadMessages',
  Render: props => {
    const { unreadPregnancyMessages, unreadPatientMessages, unreadThreadMessages } =
      props.data ?? {}

    return (
      <UnreadMessagePreviewPopover practiceId={practiceId} {...props}>
        <Center cursor="pointer" w="100%" flex={1}>
          {practiceId ? (
            <PracticeUnreadMessagesBadge unread={!!unreadThreadMessages} />
          ) : (
            <UnreadMessagesBadge
              assessmentUnread={!!unreadPregnancyMessages}
              patientUnread={!!unreadPatientMessages}
            />
          )}
        </Center>
      </UnreadMessagePreviewPopover>
    )
  },
  mobile: {
    width: 100,
    Render: props => {
      const { unreadThreadMessages } = props.data ?? {}
      return (
        <UnreadMessagePreviewPopover practiceId={practiceId} mobile {...props}>
          <Center
            boxShadow="1px 1px 3px #00000077"
            gap={1}
            bg={unreadThreadMessages ? colors.green.hex : 'gray.100'}
            borderRadius={4}
            w="100%"
            height="28px"
            flex={1}>
            <EmailIcon height="16px" color={!unreadThreadMessages ? 'gray.500' : 'white'} />
            <Text
              lineHeight={1}
              fontFamily="Hero-New"
              fontSize="xs"
              textShadow={unreadThreadMessages ? '1px 1px 3px #00000077' : 'none'}
              fontWeight={600}
              color={unreadThreadMessages ? 'white' : 'gray.500'}>
              {unreadThreadMessages} UNREAD
            </Text>
          </Center>
        </UnreadMessagePreviewPopover>
      )
    },
  },
})

export const getAssignedAdminsColumn = (
  type: 'assessment' | 'claim',
): DataColumn<AssignedAdminsItem> => ({
  title: 'Urgent',
  Header: () => (
    <Box px={1}>
      <AssignedAdminsHeader urgentKey="isUrgent" />
    </Box>
  ),
  // sortKey: 'urgentSort',
  flexProps: { justify: 'center' },
  width: 40,
  Render: ({ data, admins }) => (
    <AssignedAdmins
      data={data}
      admins={admins}
      type={type}
      urgentCollection={ASSESSMENTS_ADMIN}
      collection={ASSESSMENT_SNIPPETS}
    />
  ),
  mobile: {
    width: 40,
    Render: ({ data, admins }) => (
      <AssignedAdmins
        data={data}
        admins={admins}
        type={type}
        urgentCollection={ASSESSMENTS_ADMIN}
        collection={ASSESSMENT_SNIPPETS}
      />
    ),
  },
})
